import { reactive } from 'vue'

/**
 * Composable générique pour la gestion du tri de tableaux
 * 
 * @param {Object} options - Configuration du tri
 * @param {string} options.defaultColumn - Colonne de tri par défaut
 * @param {string} options.defaultOrder - Ordre de tri par défaut ('ASC' ou 'DESC')
 * @param {Function} options.onSort - Callback appelé après changement de tri
 */
export function useSortableTable(options = {}) {
  const {
    defaultColumn = 'id',
    defaultOrder = 'ASC',
    onSort = null
  } = options

  const sort = reactive({
    column: defaultColumn,
    order: defaultOrder
  })

  /**
   * Change la colonne/ordre de tri
   */
  const sortBy = (column) => {
    if (sort.column === column) {
      // Toggle order si même colonne
      sort.order = sort.order === 'ASC' ? 'DESC' : 'ASC'
    } else {
      // Nouvelle colonne, défaut ASC
      sort.column = column
      sort.order = 'ASC'
    }
    
    if (onSort) {
      onSort(sort.column, sort.order)
    }
  }

  /**
   * Retourne l'icône de tri appropriée
   */
  const getSortIcon = (column) => {
    if (sort.column !== column) return 'sort'
    return sort.order === 'ASC' ? 'sort-up' : 'sort-down'
  }

  /**
   * Vérifie si une colonne est active
   */
  const isSortActive = (column) => sort.column === column

  /**
   * Réinitialise le tri aux valeurs par défaut
   */
  const resetSort = () => {
    sort.column = defaultColumn
    sort.order = defaultOrder
    if (onSort) {
      onSort(sort.column, sort.order)
    }
  }

  /**
   * Fonction utilitaire pour trier un tableau côté client
   * Supporte les dates, nombres et chaînes
   */
  const sortArray = (array, column = sort.column, order = sort.order) => {
    return [...array].sort((a, b) => {
      let aVal = a[column] ?? ''
      let bVal = b[column] ?? ''
      
      // Détecter les prix/montants
      if (typeof aVal === 'string' && (aVal.includes('€') || aVal.includes('EUR'))) {
        aVal = parseFloat(aVal.replace(/[^\d,.-]/g, '').replace(',', '.')) || 0
        bVal = parseFloat(bVal.replace(/[^\d,.-]/g, '').replace(',', '.')) || 0
        return order === 'ASC' ? aVal - bVal : bVal - aVal
      }
      
      // Détecter les dates format DD/MM/YYYY
      if (typeof aVal === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(aVal)) {
        const parseDate = (str) => {
          if (!str) return null
          const parts = str.split('/')
          if (parts.length === 3) {
            return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]))
          }
          return new Date(str)
        }
        const aDate = parseDate(aVal)
        const bDate = parseDate(bVal)
        if (!aDate && !bDate) return 0
        if (!aDate) return 1
        if (!bDate) return -1
        return order === 'ASC' ? aDate - bDate : bDate - aDate
      }
      
      // Détecter les dates ISO
      if (typeof aVal === 'string' && /^\d{4}-\d{2}-\d{2}/.test(aVal)) {
        const aDate = new Date(aVal)
        const bDate = new Date(bVal)
        if (isNaN(aDate) && isNaN(bDate)) return 0
        if (isNaN(aDate)) return 1
        if (isNaN(bDate)) return -1
        return order === 'ASC' ? aDate - bDate : bDate - aDate
      }
      
      // Détecter les nombres
      if (typeof aVal === 'number' || (typeof aVal === 'string' && !isNaN(parseFloat(aVal)))) {
        aVal = parseFloat(aVal) || 0
        bVal = parseFloat(bVal) || 0
        return order === 'ASC' ? aVal - bVal : bVal - aVal
      }
      
      // Comparaison de chaînes
      aVal = String(aVal).toLowerCase()
      bVal = String(bVal).toLowerCase()
      if (aVal < bVal) return order === 'ASC' ? -1 : 1
      if (aVal > bVal) return order === 'ASC' ? 1 : -1
      return 0
    })
  }

  return {
    sort,
    sortBy,
    getSortIcon,
    isSortActive,
    resetSort,
    sortArray
  }
}
